import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

interface LLMConfigData {
  provider: string;
  model: string;
  summary_prompt: string;
  scoring_prompt: string;
  available_providers: string[];
  available_models: Record<string, string[]>;
}

const LLMConfig: React.FC = () => {
  const queryClient = useQueryClient();
  const [summaryPrompt, setSummaryPrompt] = useState('');
  const [scoringPrompt, setScoringPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch current configuration
  const { data, isLoading, isError } = useQuery({
    queryKey: ['llm-config'],
    queryFn: async () => {
      const response = await api.get('/api/admin/llm/config');
      const config = response.data.data as LLMConfigData;
      setSummaryPrompt(config.summary_prompt);
      setScoringPrompt(config.scoring_prompt);
      setSelectedModel(config.model);
      return config;
    },
  });

  // Update configuration mutation
  const updateMutation = useMutation({
    mutationFn: async (config: { summary_prompt: string; scoring_prompt: string; model: string }) => {
      const response = await api.put('/api/admin/llm/config', config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-config'] });
      setSuccessMessage('Configuration mise à jour avec succès !');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 5000);
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.error?.message || 'Erreur lors de la mise à jour');
      setSuccessMessage('');
    },
  });

  // Reset prompts mutation
  const resetMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/admin/llm/reset-prompts');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-config'] });
      setSuccessMessage('Prompts réinitialisés aux valeurs par défaut !');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 5000);
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.error?.message || 'Erreur lors de la réinitialisation');
      setSuccessMessage('');
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      summary_prompt: summaryPrompt,
      scoring_prompt: scoringPrompt,
      model: selectedModel,
    });
  };

  const handleReset = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser les prompts aux valeurs par défaut ?')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Erreur lors du chargement de la configuration LLM
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Configuration LLM
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Current Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Configuration Actuelle
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
          <Chip label={`Provider: ${data?.provider}`} color="primary" />
        </Box>

        {/* Model Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Modèle LLM</InputLabel>
          <Select
            value={selectedModel}
            label="Modèle LLM"
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            {data?.available_models[data.provider]?.map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Alert severity="info" icon={<InfoIcon />}>
          Le modèle sélectionné sera utilisé pour toutes les nouvelles analyses. Pour changer de provider (OpenAI, Anthropic, etc.), modifiez le fichier <code>backend/.env</code>.
        </Alert>
      </Paper>

      {/* Summary Prompt */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Prompt de Synthèse
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Ce prompt est utilisé pour générer les synthèses d'articles. Utilisez <code>{'{title}'}</code> et <code>{'{content}'}</code> comme placeholders.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={12}
          value={summaryPrompt}
          onChange={(e) => setSummaryPrompt(e.target.value)}
          variant="outlined"
          placeholder="Entrez le prompt de synthèse..."
          sx={{
            fontFamily: 'monospace',
            '& textarea': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
        />
      </Paper>

      {/* Scoring Prompt */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Prompt de Score de Pertinence
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Ce prompt est utilisé pour calculer le score de pertinence (0-100). Utilisez <code>{'{title}'}</code> et <code>{'{summary}'}</code> comme placeholders.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={12}
          value={scoringPrompt}
          onChange={(e) => setScoringPrompt(e.target.value)}
          variant="outlined"
          placeholder="Entrez le prompt de scoring..."
          sx={{
            fontFamily: 'monospace',
            '& textarea': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
        />
      </Paper>

      {/* Actions */}
      <Box display="flex" gap={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<ResetIcon />}
          onClick={handleReset}
          disabled={resetMutation.isPending}
        >
          Réinitialiser
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Help Section */}
      <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          💡 Conseils d'Utilisation
        </Typography>
        <Typography variant="body2" component="div">
          <ul>
            <li>Les placeholders <code>{'{title}'}</code>, <code>{'{content}'}</code>, et <code>{'{summary}'}</code> seront automatiquement remplacés</li>
            <li>Pour la synthèse, limitez à 300 mots maximum pour de meilleurs résultats</li>
            <li>Pour le score, demandez explicitement un nombre entre 0 et 100</li>
            <li>Testez vos prompts après modification en déclenchant un nouveau scraping</li>
            <li>Les modifications sont appliquées immédiatement, pas besoin de redémarrer</li>
          </ul>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LLMConfig;
